package dev.danko.roblox_craft_ai.shared.model

import kotlinx.serialization.Serializable

/**
 * The user's choice between a cheaper and a better (more expensive) generation, separately for the sketch and for
 * the 3D model: different projects need different trade-offs, so every level stays available.
 * Sketches use STANDARD and HIGH (MAX draws like HIGH); 3D models use all three.
 */
@Serializable
enum class GenerationQuality { STANDARD, HIGH, MAX }

@Serializable
data class ConceptGenerationRequest(
    val prompt: String,
    val style: String = "STYLIZED_ANIME",
    val referenceImageUrls: List<String> = emptyList(),
    val interactiveApproval: Boolean = true,
    /** Sketch quality: STANDARD = fast cheap model, HIGH = model that follows the prompt much more closely. */
    val quality: GenerationQuality = GenerationQuality.STANDARD,
    /** How many sketches to draw at once (1..[MAX_SKETCH_VARIANTS]); the user picks one for the 3D model. */
    val variants: Int = 1
)

const val MAX_SKETCH_VARIANTS = 4

@Serializable
data class ConceptGenerationResult(
    val conceptId: String,
    val prompt: String,
    val expandedPrompt: String,
    val imageUrl: String,
    val status: String, // "PENDING", "READY", "FAILED"
    val errorMessage: String? = null,
    val quality: GenerationQuality = GenerationQuality.STANDARD,
    /** All sketches of the last drawing, [imageUrl] first; one of them is sent back when the 3D model is made. */
    val variantUrls: List<String> = emptyList()
)

@Serializable
data class ApproveConceptRequest(
    val conceptId: String,
    val approvedImageUrl: String,
    /** Triangles of the Roblox model: 500..20 000, or 0 to keep the generator's detailed mesh (high-poly). */
    val targetPolycount: Int = RobloxMeshLimits.DEFAULT_TRIANGLES,
    /** Texture the generator paints: 1024, 2048 or 4096 px; the Roblox model always gets 1024. */
    val textureSize: Int = 1024,
    val preferred3dProvider: String? = null, // "meshy-image-to-3d", "tripo3d", "mock-3d"
    /** 3D quality: STANDARD = fast cheap model, HIGH = newer model with cleaner shapes and textures. */
    val quality: GenerationQuality = GenerationQuality.STANDARD
)

@Serializable
data class RerollConceptRequest(
    val conceptId: String,
    val additionalPrompt: String? = null,
    val quality: GenerationQuality = GenerationQuality.STANDARD,
    val variants: Int = 1
)
